import React, { useState, useRef, useEffect } from 'react';
import { Task, CreateTaskInput } from '../../services/taskService';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { ClipboardList, PlusIcon, FilterIcon, ExternalLink, Calendar, Edit, Trash2, CheckCircle } from 'lucide-react';
import { TaskModal } from './TaskModal';
import { TaskForm } from './TaskForm';
import { taskService } from '../../services/taskService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Card } from '../ui/card';
import { format, isToday, isYesterday, isTomorrow, isSameDay } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { supabase } from '@/lib/supabaseClient';
import { TaskDetails } from './TaskList';

const formatRelativeDate = (date: Date): string => {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  if (isTomorrow(date)) return 'Tomorrow';
  return format(date, 'MMM d, yyyy');
};

interface TaskDropdownProps {
  currentProjectId?: string;
}

export const TaskDropdown: React.FC<TaskDropdownProps> = ({ currentProjectId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('active');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [assignerProfiles, setAssignerProfiles] = useState<Record<string, { name: string }>>({});
  const channelRef = useRef<any>(null);
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [selectOpen, setSelectOpen] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUserId(user?.id || null);
      setUserId(user?.id || null);
    });
  }, []);

  useEffect(() => {
    if (!currentUserId) return;
    // Subscribe to changes for tasks assigned to the current user
    const channel = supabase.channel(`quick_tasks_changes_${currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks',
          filter: `assignee_id=eq.${currentUserId}`
        },
        (payload) => {
          loadTasks();
        }
      )
      .subscribe();
    channelRef.current = channel;
    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [currentUserId]);

  const loadTasks = async () => {
    setIsLoading(true);
    try {
      const data = await taskService.getAssignedToMeTasks();
      setTasks(data);
      // Fetch assigner profiles for tasks not assigned by current user
      const assignerIds = Array.from(new Set(data.filter(t => t.user_id && t.user_id !== currentUserId).map(t => t.user_id)));
      if (assignerIds.length > 0) {
        const { data: profiles } = await supabase
          .from('users')
          .select('id, first_name, last_name, email')
          .in('id', assignerIds);
        if (profiles) {
          const profileMap: Record<string, { name: string }> = {};
          profiles.forEach((p: any) => {
            let name = '';
            if (p.first_name && p.last_name) name = `${p.first_name} ${p.last_name}`;
            else if (p.first_name) name = p.first_name;
            else if (p.last_name) name = p.last_name;
            else if (p.email) name = p.email.split('@')[0];
            else name = `User ${p.id.substring(0, 4)}`;
            profileMap[p.id] = { name };
          });
          setAssignerProfiles(profileMap);
        }
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      loadTasks();
    }
    setIsOpen(!isOpen);
  };

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      
      // Don't close if clicking inside select or its portal
      const isSelectClick = 
        target.closest('[role="listbox"]') !== null || 
        target.closest('[role="combobox"]') !== null ||
        target.closest('[data-radix-select-trigger]') !== null ||
        target.closest('[data-radix-select-content]') !== null;
      
      if (isSelectClick) {
        return;
      }
      
      // Only close dropdown if clicking outside both dropdown and select
      if (dropdownRef.current && 
          !dropdownRef.current.contains(event.target as Node)) {
        // Don't close if select is open
        if (!selectOpen) {
          setIsOpen(false);
        }
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectOpen]);

  const handleCreateTask = async (task: CreateTaskInput) => {
    try {
      const newTask = await taskService.createTask(task);
      setTasks([newTask, ...tasks]);
      setShowTaskForm(false);
      setSelectedTask(newTask);
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleTaskStatusChange = async (task: Task, checked: boolean) => {
    try {
      // Optimistically update the UI
      if (checked) {
        setTasks(tasks.filter(t => t.id !== task.id));
      }
      const updatedTask = checked 
        ? await taskService.completeTask(task.id)
        : await taskService.uncompleteTask(task.id);
      
      // Only update the selected task if it's being viewed
      if (selectedTask?.id === task.id) {
        setSelectedTask(updatedTask);
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      // Revert the optimistic update if there's an error
      if (checked) {
        loadTasks();
      }
    }
  };

  const handleUpdateTask = async (id: string, updates: Partial<Task>) => {
    try {
      const updatedTask = await taskService.updateTask(id, updates);
      setTasks(tasks.map(t => t.id === id ? updatedTask : t));
      if (selectedTask?.id === id) {
        setSelectedTask(updatedTask);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteTask = async (id: string) => {
    try {
      await taskService.deleteTask(id);
      setTasks(tasks.filter(t => t.id !== id));
      setSelectedTask(null);
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleViewTask = (task: Task) => {
    setSelectedTask(task);
    setIsOpen(false);
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'all') return true;
    if (filter === 'active') return task.status !== 'completed';
    if (filter === 'completed') return task.status === 'completed';
    return true;
  });

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleToggle}
          className="relative"
        >
          <ClipboardList className="h-5 w-5" />
          {tasks.filter(task => task.status !== 'completed').length > 0 && (
            <Badge
              variant="secondary"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0"
            >
              {tasks.filter(task => task.status !== 'completed').length}
            </Badge>
          )}
        </Button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-80 bg-popover text-popover-foreground rounded-lg shadow-lg border border-border z-[250]">
            <div className="p-4 border-b relative">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-foreground">Quick Tasks</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setShowTaskForm(true);
                    setIsOpen(false);
                  }}
                >
                  <PlusIcon className="h-4 w-4" />
                </Button>
              </div>
              <Select 
                value={filter} 
                onValueChange={(value: 'all' | 'active' | 'completed') => {
                  setFilter(value);
                }}
                onOpenChange={setSelectOpen}
                open={selectOpen}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Filter tasks" />
                </SelectTrigger>
                <SelectContent 
                  position="popper" 
                  className="z-[9999] w-[var(--radix-select-trigger-width)]"
                  sideOffset={5}
                  align="start"
                  side="bottom"
                  onCloseAutoFocus={(e) => {
                    // Prevent focus management from closing the dropdown
                    e.preventDefault();
                  }}
                  onEscapeKeyDown={(e) => {
                    // Prevent escape from closing the parent dropdown
                    e.stopPropagation();
                  }}
                  style={{
                    // Ensure proper stacking context
                    transform: 'translateZ(0)',
                    willChange: 'transform'
                  }}
                >
                  <SelectItem value="all">All Tasks</SelectItem>
                  <SelectItem value="active">Active Tasks</SelectItem>
                  <SelectItem value="completed">Completed Tasks</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <ScrollArea className="h-[300px]">
              {isLoading && tasks.length === 0 ? (
                <div className="p-4 text-center text-foreground/60">
                  Loading tasks...
                </div>
              ) : filteredTasks.length === 0 ? (
                <div className="p-4 text-center text-foreground/60">
                  No tasks found
                </div>
              ) : (
                <div 
                  className="p-2 grid gap-2"
                  style={{
                    gridAutoRows: 'minmax(0, auto)',
                    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  {filteredTasks.map(task => (
                    <div
                      key={task.id}
                      className={`flex items-start gap-2 p-2 rounded-lg group cursor-pointer transition-all duration-300 ease-in-out ${
                        task.priority === 'high'
                          ? 'bg-destructive/5'
                          : task.priority === 'medium'
                          ? 'bg-primary/5'
                          : 'bg-success/5'
                      } hover:bg-muted ${
                        completingTaskId === task.id 
                          ? 'translate-x-32 opacity-0 pointer-events-none scale-95' 
                          : 'transform-gpu'
                      }`}
                      style={{
                        transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                        willChange: 'transform, opacity',
                      }}
                      onClick={() => handleViewTask(task)}
                    >
                      <button
                        onClick={async e => {
                          e.stopPropagation();
                          setCompletingTaskId(task.id);
                          setTimeout(async () => {
                            await handleTaskStatusChange(task, true);
                            setCompletingTaskId(null);
                          }, 300);
                        }}
                        className="mt-1 focus:outline-none"
                        aria-label="Mark as complete"
                      >
                        <CheckCircle 
                          size={22} 
                          className="text-primary hover:scale-110 transition-transform" 
                        />
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${task.status === 'completed' ? 'line-through text-foreground/60' : 'text-foreground'}`}>
                          {task.title}
                        </p>
                        {task.user_id && task.user_id !== currentUserId && assignerProfiles[task.user_id]?.name && (
                          <div className="text-xs text-foreground/60 leading-tight">
                            Assigned by: {assignerProfiles[task.user_id]?.name}
                          </div>
                        )}
                        {task.due_date && (
                          <p className="text-xs text-foreground/60 mt-1">
                            Due: {formatRelativeDate(new Date(task.due_date))}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            <div className="p-2 border-t flex justify-center">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/app/tasks');
                }}
              >
                View All Tasks
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog 
        open={!!selectedTask || showTaskForm} 
        onOpenChange={(open) => {
          if (!open) {
            setSelectedTask(null);
            setShowTaskForm(false);
            setIsEditing(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {showTaskForm ? 'Create New Task' : 'Task Details'}
            </DialogTitle>
            <DialogDescription>
              {showTaskForm ? 'Create a new task' : 'View and manage your task'}
            </DialogDescription>
          </DialogHeader>
          {showTaskForm && (
            <div className="flex-1 overflow-y-auto min-h-0" style={{ overscrollBehavior: 'contain' }}>
              <TaskForm
                onSubmit={handleCreateTask}
                onCancel={() => setShowTaskForm(false)}
                inDialog={true}
                task={currentProjectId ? {
                  id: '',
                  project_id: currentProjectId,
                  title: '',
                  description: '',
                  priority: 'medium',
                  status: 'todo',
                  user_id: '',
                  assignee_id: '',
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                } as Task : undefined}
              />
            </div>
          )}
          {!showTaskForm && selectedTask && !isEditing && (
            <TaskDetails
              task={selectedTask}
              onEdit={() => setIsEditing(true)}
              onDelete={async () => {
                await handleDeleteTask(selectedTask.id);
                setSelectedTask(null);
              }}
              userId={userId}
              onComplete={async (id) => {
                await handleTaskStatusChange(selectedTask, true);
                setSelectedTask(null);
              }}
              onUncomplete={async (id) => {
                await handleTaskStatusChange(selectedTask, false);
                setSelectedTask({ ...selectedTask, status: 'todo', completed_at: undefined });
              }}
              onClose={() => setSelectedTask(null)}
            />
          )}
          {!showTaskForm && selectedTask && isEditing && (
            <div className="flex-1 overflow-y-auto min-h-0" style={{ overscrollBehavior: 'contain' }}>
              <TaskForm
                task={selectedTask}
                onSubmit={async (updates) => {
                  await handleUpdateTask(selectedTask.id, updates);
                  setIsEditing(false);
                }}
                onCancel={() => setIsEditing(false)}
                inDialog={true}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}; 